"use client";
import CustomButton from '@/components/ui/custom/CustomButton';
import {cn, handleScrollToId} from "@/lib/utils";
import {gsap, ScrollTrigger} from '@/lib/gsap'; // Adjust path as needed
import {ArrowBigDownDash} from "lucide-react";
import { OptimizedImage } from '@/components/ui/OptimizedImage';
import React, {useEffect, useRef, useState} from 'react';

interface HeroProps {
    className?: string
    parentContainerRef?: React.RefObject<HTMLElement>;
}

export default function HeroV2({className}: HeroProps) {
    const heroRef = useRef<HTMLDivElement>(null);
    const bouncingArrowRef = useRef<HTMLDivElement>(null);
    const headlineRef = useRef<HTMLHeadingElement>(null);
    const subheadlineRef = useRef<HTMLParagraphElement>(null);
    const ctaRef = useRef<HTMLDivElement>(null);
    const trademarkRef = useRef<HTMLElement>(null);
    const imageRef = useRef<HTMLImageElement>(null);

    const [showBouncingArrow, setShowBouncingArrow] = useState(true);

    useEffect(() => {
        // GSAP animations
        gsap.fromTo(
            headlineRef.current,
            {y: 50, opacity: 0},
            {y: 0, opacity: 1, duration: 1, ease: 'power3.out'}
        );
        gsap.fromTo(
            subheadlineRef.current,
            {y: 50, opacity: 0},
            {y: 0, opacity: 1, duration: 1, delay: 0.3, ease: 'power3.out'}
        );
        gsap.fromTo(
            ctaRef.current,
            {scale: 0.8, opacity: 0},
            {scale: 1, opacity: 1, duration: 0.8, delay: 0.6, ease: 'elastic.out(1, 0.5)'}
        );

        gsap.fromTo(
            trademarkRef.current,
            {scale: 0, opacity: 0},
            {scale: 1, opacity: 1, duration: 0.8, delay: 1.6, ease: 'elastic.out(1, 0.5)'}
        );
        gsap.fromTo(
            bouncingArrowRef.current,
            {opacity: 0},
            {opacity: 1, duration: 0.5, delay: 1.5, ease: 'power2.out'}
        );
        gsap.to(bouncingArrowRef.current, {
            y: 10, // Move down 10px
            duration: 0.8,
            repeat: -1, // Infinite loop
            yoyo: true, // Reverse back to original position
            ease: 'power1.inOut', // Smooth bounce-like easing
        });


        // Arrow fade-out with ScrollTrigger
        gsap.to(bouncingArrowRef.current, {
            opacity: 0,
            duration: 1,
            ease: 'power2.in',
            scrollTrigger: {
                trigger: heroRef.current,
                start: 'bottom bottom', // Start when hero's bottom hits viewport bottom
                end: '+=200', // End after 100px of scrolling
                scrub: true, // Smoothly animate with scroll
                onLeave: () => {
                    // Hide after animation
                    gsap.set(bouncingArrowRef.current, {display: 'none'})
                },
                onEnterBack: () => {
                     // Show if scrolling back

                    // gsap.set(bouncingArrowRef.current, {display: 'block'})
                }

            },
        });

        // TODO: revisit hero background moving (i dont see the benefit)
        const animateImage = false;

        if (animateImage) {
            // Background parallax with ScrollTrigger
            gsap.to(imageRef.current, {
                yPercent: -30, // Move up 30% of image height
                ease: 'none',
                scrollTrigger: {
                    trigger: heroRef.current,
                    start: 'top top', // Start when hero's top hits viewport top
                    end: 'bottom top', // End when hero's bottom hits viewport top
                    scrub: true, // Sync with scroll
                },
            });
        }

        // Cleanup
        return () => {
            ScrollTrigger.getAll().forEach((trigger) => trigger.kill());
        };
    }, []);

    return (
        <div
            ref={heroRef}
            className={cn("relative flex items-center justify-center min-h-screen text-white overflow-hidden w-full", className)}
        >
            {/* Background Image with next/image */}
            <OptimizedImage
                ref={imageRef}
                src="/images/promo/audience/audience-laughing-5.jpg"
                alt="Audience Laughing"
                fill
                style={{objectFit: 'cover'}}
                quality={75}
                priority // Load immediately for hero
                loading={"eager"}
                className="z-0 object-center sm:object-[20%_50%] md:object-[70%_50%] lg:object-[80%_50%]"
            />

            {/* Overlay for text readability */}
            <div className="absolute inset-0 bg-black opacity-50 z-10"/>

            {/* Content */}
            <div className="relative z-20 text-center px-4">
                <h1
                    ref={headlineRef}
                    className="text-7xl md:text-9xl mb-4 drop-shadow-lg font-don"
                    // TODO: double check what we want to do w/ the textShadow
                    style={{'textShadow': '6px -7px 4px rgba(0,0,0,0.22)'}}
                >
                    Standup Comedy<br/> Events

                    <sup ref={trademarkRef}
                         className={cn("font-sans text-3xl relative",
                             "-left-2",
                             "-top-8",
                             "md:text-5xl md:-top-14")}>&trade;</sup>

                </h1>
                <p
                    ref={subheadlineRef}
                    className="text-2xl md:text-3xl mb-8 opacity-90"
                >
                    in Cambridge & <em>Beyond!</em>
                </p>
                <div ref={ctaRef} className={"my-10"}>

                    <div className="flex flex-col items-center space-y-4">
                        <div className="max-w-xl w-full px-4">
                            <CustomButton
                                className={cn(
                                    "h-10 text-2xl",
                                    "md:h-20 md:text-3xl",
                                    "drop-shadow-2xl"
                                )}
                                onClick={() => {
                                    handleScrollToId('events')
                                }}
                                text="See Upcoming Events"
                                showArrow={false}/>
                        </div>

                        <div
                            ref={bouncingArrowRef}
                            className={cn("z-20",
                                // "animate-bounce",
                                // "left-1/2 transform -translate-x-1/2"
                            )}
                            onClick={() => handleScrollToId('events')}
                        >
                            <ArrowBigDownDash size={40} strokeWidth={2.25}/>
                        </div>

                    </div>
                </div>
            </div>
        </div>
    );
}
